/* eslint-disable react/require-default-props */
import React, { useMemo, useState } from 'react';
import { Row, Col, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Files } from 'ontraccr-common';

import InvisibleButton from '../../buttons/InvisibleButton';
import ProgressWheel from '../../ProgressWheel';

import VideoIcon from '../../icons/files/VideoIcon';
import SpreadsheetIcon from '../../icons/files/SpreadsheetIcon';
import PDFIcon from '../../icons/files/PDFIcon';
import DocumentIcon from '../../icons/files/DocumentIcon';
import FileIcon from '../../icons/files/FileIcon';
import TrashIcon from '../../icons/files/TrashIcon';

import { FILE_TYPE } from './fileTile.constants';

import { visuallyDownloadFile, downloadFile, getFileSignedDownloadUrl } from '../../../files/fileHelpers';
import { decorateFile } from '../../../forms/formHelpers';
import { toTitleCase } from '../../../helpers/helpers';

const SUPPORTED_FILE_TYPES = new Set(['image', 'video', 'spreadsheet', 'pdf', 'document']);

function FileTile({
  file,
  onClick = null,
  canDownload = false,
  onDelete = null,
  titleAsTooltip = false,
  dimensions = 64,
  containerStyle = {},
  shouldImageCoverContainer = false,
  externalTemplateId = null,
}) {
  const dispatch = useDispatch();
  const cursor = onClick ? 'pointer' : 'auto';
  const globalFileMap = useSelector((state) => state.files.fileMap);
  const [progress, setProgress] = useState(0);
  const [hasError, setHasError] = useState(false);

  const loading = progress > 0 && progress < 100;
  const dimensionRatio = shouldImageCoverContainer ? 1.5 : 1;

  const trueId = file?.id || file?.autoSaveId;
  const globalFile = file instanceof File ? file : globalFileMap[trueId];
  const src = (globalFile && globalFile instanceof File && !file.thumbnailURL)
    ? URL.createObjectURL(globalFile)
    : file.thumbnailURL;

  let fileType = globalFile?.type ?? file?.type ?? file?.jsFileObject?.type;

  if ((!fileType || !SUPPORTED_FILE_TYPES.has(fileType)) && file?.name) {
    fileType = Files.getFileType(file);
  }

  const handleClick = async (e) => {
    e.stopPropagation();
    if (!onClick) return;
    // Files without id (e.g. new uploads) cannot be loaded from server; let parent handle preview (e.g. object URL)
    if (!file?.id && !file?.autoSaveId) {
      onClick();
      return;
    }
    if (!globalFile || !(globalFile instanceof File)) {
      // External forms serve shallow files with a signed url; preview via url without
      // loading into the global file map. Internal/board card views must download first.
      if (externalTemplateId && file?.url) {
        onClick();
        return;
      }
      visuallyDownloadFile({
        dispatch,
        setProgress,
        file,
        externalTemplateId,
      }).then((success) => {
        if (success) {
          onClick();
        }
      });
      return;
    }
    onClick();
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (!canDownload) return;

    if (globalFile instanceof File) { // files already in the file map (ex. clicked file to view)
      downloadFile({ fileObject: globalFile });
    } else if (file?.url) {
      downloadFile({
        fileURL: file.url,
        saveName: file.name,
      });
    } else if (file.path) { // static attachments
      const rawFullName = `${file.path}/${file.name}`;
      downloadFile({
        fileDetails: {
          name: file.name,
          fullPath: encodeURIComponent(rawFullName),
        },
        externalTemplateId,
      });
    } else if (trueId) { // files not in the file map (not viewed)
      // Get signed URL from backend, use decorateFile to fetch and create File object
      const signedUrl = await getFileSignedDownloadUrl({
        fileId: trueId,
        externalTemplateId,
      });
      if (signedUrl) {
        const fileObject = await decorateFile({ url: signedUrl, name: file.name }, trueId);
        if (fileObject) {
          downloadFile({ fileObject });
        }
      }
    } else { // this just opens the file in a new tab
      downloadFile({
        fileURL: file.url,
        saveName: file.name,
      });
    }
  };

  const imageStyle = shouldImageCoverContainer ? {
    width: '100%',
    height: '100%',
    minWidth: dimensions,
    minHeight: dimensions,
    maxHeight: dimensions,
    maxWidth: dimensions,
    display: 'block',
  } : {
    width: dimensions,
    height: dimensions,
    top: 0
  };

  const buttonStyle = shouldImageCoverContainer ? {
    overflow: 'hidden',
    width: dimensions,
    minWidth: dimensions,
    borderRadius: 4,
    display: 'block',
  } : {};

  const icon = useMemo(() => {
    if (loading) {
      return <ProgressWheel percentage={progress} style={{ position: 'absolute' }} />;
    }
    switch (fileType) {
      case 'image':
        return (
          <img
            src={src}
            key={file?.id}
            alt="Post"
            onError={() => setHasError(true)}
            style={{
              cursor,
              transform: `rotate(${file.rotation}deg)`,
              borderRadius: 4,
              objectFit: 'cover',
              ...imageStyle,
            }}
          />
        );
      case 'video':
        return <VideoIcon width={dimensions / dimensionRatio} height={dimensions / dimensionRatio} />;
      case 'spreadsheet':
        return <SpreadsheetIcon width={dimensions / dimensionRatio} height={dimensions / dimensionRatio} />;
      case 'pdf':
        return <PDFIcon width={dimensions / dimensionRatio} height={dimensions / dimensionRatio} />;
      case 'document':
        return <DocumentIcon width={dimensions / dimensionRatio} height={dimensions / dimensionRatio} />;
      default:
        return <FileIcon width={dimensions / dimensionRatio} height={dimensions / dimensionRatio} />;
    }
  }, [fileType, src, file.rotation, loading, hasError, cursor, dimensions, dimensionRatio]);

  const tileContent = (
    <Row className="file-tile" style={containerStyle}>
      <Col span="64px">
        <InvisibleButton
          style={{
            height: '100%',
            ...buttonStyle,
          }}
          onClick={handleClick}
        >
          {icon}
        </InvisibleButton>
      </Col>
      {!titleAsTooltip && (
        <Col className="file-tile-text-container">
          <div className="file-tile-text-container-name">
            {file.name}
          </div>
          <div className="file-tile-text-container-type">
            {fileType === 'pdf' ? fileType.toUpperCase() : toTitleCase(fileType)}
          </div>
        </Col>
      )}
      {
        canDownload && (
          <Col span="20px" onClick={handleDownload}>
            <Row justify="center" align="middle" className="file-tile-download-icon">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M14.6667 14C14.6667 14.3682 14.3682 14.6667 14 14.6667H0.666667C0.298477 14.6667 0 14.3682 0 14C0 13.6318 0.298477 13.3333 0.666667 13.3333H14C14.3682 13.3333 14.6667 13.6318 14.6667 14Z" fill="#3C5873" />
                <path fillRule="evenodd" clipRule="evenodd" d="M8.20658 11.7921C7.70543 12.2264 6.96125 12.2264 6.4601 11.7921L3.24725 9.00759C2.31452 8.19922 2.88622 6.66667 4.12049 6.66667L5.33334 6.66667V1.33333C5.33334 0.596953 5.93029 1.8094e-07 6.66667 1.16564e-07L8.00001 0C8.73639 -6.43763e-08 9.33334 0.596954 9.33334 1.33333L9.33334 6.66667H10.5462C11.7805 6.66667 12.3522 8.19922 11.4194 9.00759L8.20658 11.7921ZM6.89672 10.4061C7.1473 10.6232 7.51939 10.6232 7.76996 10.4061L10.5462 8H9.00001C8.44772 8 8.00001 7.55228 8.00001 7V1.33333L6.66667 1.33333V7C6.66667 7.55228 6.21896 8 5.66667 8L4.12049 8L6.89672 10.4061Z" fill="#3C5873" />
              </svg>
            </Row>
          </Col>
        )
      }
      {onDelete && (
        <Col span="20px" onClick={onDelete} style={{ marginLeft: 5 }}>
          <Row justify="center" align="middle" className="file-tile-download-icon">
            <TrashIcon width={12} height={15} />
          </Row>
        </Col>
      )}
    </Row>
  );

  if (titleAsTooltip && file?.name) {
    return (
      <Tooltip title={file.name} placement="top">
        {tileContent}
      </Tooltip>
    );
  }

  return tileContent;
}

FileTile.propTypes = {
  onClick: PropTypes.func,
  file: FILE_TYPE.isRequired,
  canDownload: PropTypes.bool,
  onDelete: PropTypes.func,
  titleAsTooltip: PropTypes.bool,
  dimensions: PropTypes.number,
  containerStyle: PropTypes.shape({}),
  shouldImageCoverContainer: PropTypes.bool,
  externalTemplateId: PropTypes.string,
};

FileTile.defaultProps = {
  containerStyle: {},
  shouldImageCoverContainer: false,
};

export default FileTile;
